import { clsx, EMPTY_STRING, intersperse, KeyNames } from "@regrello/core-utils";
import {
  RegrelloClampedText,
  RegrelloField,
  RegrelloInput,
  type RegrelloInputProps,
  RegrelloLinkify,
  type RegrelloSize,
} from "@regrello/ui-core";
import React, { useEffect, useId, useRef } from "react";

import type { RegrelloFormFieldBaseProps } from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldEmptyValueEmpty } from "./_internal/RegrelloFormFieldEmptyValueEmpty";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";
import { getSelfContainedFormInternal } from "./_internal/selfContainedForm/getSelfContainedFormInternal";

export interface RegrelloFormFieldTextMultilineProps
  extends RegrelloFormFieldBaseProps<string>,
    Pick<
      RegrelloInputProps,
      "autoFocus" | "inputRef" | "onBlur" | "onChange" | "onKeyDownCapture" | "onPaste" | "readOnly"
    > {
  /** Whether to prevent the user from inserting newlines on "Enter". */
  disableNewlines?: boolean;

  /** Whether the text field is collapsed when the component is first rendered. */
  initialState?: "collapsed" | "expanded";

  /** Additional CSS class name to apply to the input element. */
  inputClassName?: string;

  /** Whether the display value of a self-contained multiline form field component is collapsable. */
  isCollapsible?: boolean;

  /**
   * Whether the default maximum height should not be enforced. If `true`, the field will grow as
   * much as needed to display all rows of content without internal scrolling.
   */
  maxHeightDisabled?: boolean;

  /**
   * The minimum number of lines to display in the input when the input is empty.
   *
   * @default 1
   */
  minimumRowCount?: number;

  /**
   * If provided, will show an 'x' button in the input that invokes this callback when clicked.
   * Expected usage thereafter is for the callback to clear the input's value.
   */
  onClearClick?: () => void;

  /**
   * The size of the input field.
   *
   * @default RegrelloSize.MEDIUM
   */
  size?: RegrelloSize;

  /** Class name to pass to the `<textarea>` element. */
  textareaClassName?: string;

  value: string;
}

export const RegrelloFormFieldTextMultiline = React.memo(function RegrelloFormFieldTextMultilineFn({
  className,
  dataTestId,
  disableNewlines,
  error,
  endAdornment,
  helperText,
  // eslint-disable-next-line lingui/no-unlocalized-strings
  initialState = "collapsed",
  infoTooltipText,
  infoTooltipIconName,
  infoTooltipVariant,
  inputClassName,
  isDefaultMarginsOmitted,
  isDeleted,
  isCollapsible = false,
  isRequiredAsteriskShown,
  label,
  labelPlacement,
  labelWidth,
  maxHeightDisabled,
  minimumRowCount = 1,
  selfContainedForm,
  size,
  textareaClassName,
  value,
  variant = "default",
  ...textFieldProps
}: RegrelloFormFieldTextMultilineProps) {
  const id = useId();
  const hasError = error != null;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // (jullman): On mount, set the height to fit the content. Wait for the next animation frame to
  // ensure the scrollHeight is correct before accessing it, otherwise, it overestimates.
  useEffect(() => {
    requestAnimationFrame(() => {
      if (textareaRef.current != null) {
        setHeightToFitContent(textareaRef.current);
      }
    });
  }, []);

  if (variant === "spectrum" && selfContainedForm == null) {
    return (
      <RegrelloField
        dataTestId={dataTestId}
        deleted={isDeleted}
        description={infoTooltipText}
        endAdornment={endAdornment}
        errorMessage={error}
        helperText={typeof helperText === "string" ? helperText : undefined}
        htmlFor={id}
        label={label}
        name={textFieldProps.name ?? EMPTY_STRING}
        required={isRequiredAsteriskShown}
      >
        {() => (
          <RegrelloInput
            {...textFieldProps}
            asChild={true}
            autoFocus={textFieldProps.autoFocus || selfContainedForm != null}
            className={clsx("h-auto", inputClassName)}
            id={id}
            intent={hasError ? "danger" : "neutral"}
            size={size}
            value={value}
          >
            <textarea
              ref={textareaRef}
              className={clsx("my-2 resize-none min-h-5", {
                "max-h-[16rem]": !maxHeightDisabled,
              })}
              onInput={(event) => {
                setHeightToFitContent(event.currentTarget);
              }}
              onKeyDown={(event) => {
                if (disableNewlines && event.key === KeyNames.ENTER && !event.metaKey) {
                  // (clewis): Don't add a newline when "Enter" is pressed. Note that holding the meta key at
                  // the same time can trigger different reactions (e.g., submitting a form), so we enter this
                  // block only if the meta key isn't pressed.
                  event.preventDefault();
                }
              }}
              rows={minimumRowCount}
            />
          </RegrelloInput>
        )}
      </RegrelloField>
    );
  }

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      error={error}
      helperText={helperText}
      htmlFor={id}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) => {
        if (isCollapsible) {
          return (
            <RegrelloClampedText initialState={initialState} maxLines={selfContainedForm?.maxRowWhenCollapsed}>
              {savedValue}
            </RegrelloClampedText>
          );
        }

        return savedValue === EMPTY_STRING ? <RegrelloFormFieldEmptyValueEmpty /> : getDisplayValue(savedValue);
      })}
      variant={variant}
    >
      <RegrelloInput
        {...textFieldProps}
        asChild={true}
        autoFocus={textFieldProps.autoFocus || selfContainedForm != null}
        className={clsx("h-auto", inputClassName)}
        id={id}
        intent={hasError ? "danger" : "neutral"}
        size={size}
        value={value}
      >
        <textarea
          ref={textareaRef}
          className={clsx(
            "my-2 resize-none min-h-5",
            {
              "max-h-[16rem]": !maxHeightDisabled,
            },
            textareaClassName,
          )}
          onInput={(event) => {
            setHeightToFitContent(event.currentTarget);
          }}
          onKeyDown={(event) => {
            if (disableNewlines && event.key === KeyNames.ENTER && !event.metaKey) {
              // (clewis): Don't add a newline when "Enter" is pressed. Note that holding the meta key at
              // the same time can trigger different reactions (e.g., submitting a form), so we enter this
              // block only if the meta key isn't pressed.
              event.preventDefault();
            }
          }}
          rows={minimumRowCount}
        />
      </RegrelloInput>
    </RegrelloFormFieldLayout>
  );
});

function getDisplayValue(textMultilineValue: string): React.ReactNode {
  const valueWithLineBreaks = intersperse<React.ReactNode>(textMultilineValue.split("\n"), {
    type: "callback",
    callback: (_, leftIndex) => <br key={`br=${leftIndex}`} />,
  });

  return (
    <div>
      <RegrelloLinkify>{valueWithLineBreaks}</RegrelloLinkify>
    </div>
  );
}

function setHeightToFitContent(element: HTMLTextAreaElement) {
  if (element != null) {
    // (jullman): Reset the height to auto first to be able to measure the correct scrollHeight.
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px` as const;
  }
}
